import { useEffect, useMemo, useRef, useState } from "react";
import { JellyButton } from "./components/JellyButton";
import { runAudit } from "./features/audit/audit-service";
import { downloadAuditReport } from "./features/audit/report";
import type { AuditProgress, CompletedAudit } from "./features/audit/types";
import { addHistory, readHistory, type HistoryEntry } from "./features/history/history-store";

type Stage = "file" | "audit" | "report";

const emptyProgress: AuditProgress = { percent: 0, label: "", processed: 0, total: 0 };

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
}

function formatDuration(ms: number) {
  if (ms < 1000) return `${Math.max(1, Math.round(ms))} мс`;
  return `${(ms / 1000).toFixed(ms < 10_000 ? 2 : 1)} сек`;
}

function WorkflowItem({ stage, current, title, detail }: { stage: Stage; current: Stage; title: string; detail: string }) {
  const order: Stage[] = ["file", "audit", "report"];
  const state = order.indexOf(stage) < order.indexOf(current) ? "is-complete" : stage === current ? "is-active" : "";
  return (
    <div className={`workflow-item ${state}`}>
      <span className="workflow-dot" aria-hidden="true" />
      <div><strong>{title}</strong><small>{detail}</small></div>
    </div>
  );
}

function HistoryDrawer({ open, history, onClose }: { open: boolean; history: HistoryEntry[]; onClose: () => void }) {
  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [open, onClose]);

  return (
    <div className={`history-drawer ${open ? "is-open" : ""}`} aria-hidden={!open}>
      <button className="drawer-backdrop" onClick={onClose} aria-label="Закрыть историю" />
      <aside className="drawer-panel" role="dialog" aria-modal="true" aria-labelledby="history-title">
        <header className="drawer-header">
          <h2 id="history-title">История загрузок</h2>
          <button className="drawer-close" onClick={onClose} aria-label="Закрыть"><span /></button>
        </header>
        <div className="drawer-body">
          <p className="history-intro">Завершённые аудиты сохраняются только в этом браузере.</p>
          <div className="history-overview"><span>Всего анализов</span><strong>{history.length}</strong></div>
          {!history.length ? (
            <div className="history-empty"><strong>История пока пуста</strong><span>Первый завершённый аудит появится здесь автоматически.</span></div>
          ) : (
            <div className="history-list">
              {history.map((item) => {
                const created = new Date(item.createdAt);
                return (
                  <article className="history-entry" key={item.id}>
                    <div className="history-entry-head">
                      <strong>{item.sourceName}</strong>
                      <time dateTime={item.createdAt}>{created.toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</time>
                    </div>
                    <div className="history-entry-stats">
                      <span><b>{item.total.toLocaleString("ru-RU")}</b><small>событий</small></span>
                      <span><b>{item.checked.toLocaleString("ru-RU")}</b><small>проверено</small></span>
                      <span className="is-danger"><b>{item.violations}</b><small>явных</small></span>
                      <span className="is-warning"><b>{item.manual}</b><small>ручных</small></span>
                    </div>
                    <div className="history-entry-meta">{formatDuration(item.durationMs)} · правила {item.version}</div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

export function AuditorApp() {
  const inputRef = useRef<HTMLInputElement>(null);
  const toastTimer = useRef<number | undefined>(undefined);
  const [file, setFile] = useState<File | null>(null);
  const [stage, setStage] = useState<Stage>("file");
  const [progress, setProgress] = useState<AuditProgress>(emptyProgress);
  const [audit, setAudit] = useState<CompletedAudit | null>(null);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[]>(() => readHistory());
  const version = window.CallAudit?.CONFIG.version || "1.2.5";

  const extension = useMemo(() => file?.name.split(".").pop()?.toUpperCase() || "", [file]);

  const selectFile = (candidate?: File) => {
    if (!candidate) return;
    const ext = candidate.name.split(".").pop()?.toLowerCase();
    if (!ext || !["xls", "xlsx", "xlsm"].includes(ext)) {
      setError("Выберите файл Excel в формате .xls, .xlsx или .xlsm.");
      return;
    }
    setFile(candidate);
    setAudit(null);
    setError("");
    setProgress(emptyProgress);
    setStage("file");
    window.clearTimeout(toastTimer.current);
    setToast(true);
    toastTimer.current = window.setTimeout(() => setToast(false), 1000);
  };

  const startAudit = async () => {
    if (!file || busy) return;
    setError("");
    setBusy(true);
    setStage("audit");
    try {
      const completed = await runAudit(file, setProgress);
      setAudit(completed);
      setStage("report");
      setHistory(addHistory({
        sourceName: completed.sourceName,
        durationMs: completed.durationMs,
        version,
        ...completed.metrics,
      }));
    } catch (reason) {
      setStage("file");
      setError(reason instanceof Error ? reason.message : "Не удалось обработать файл.");
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    if (!audit || downloading) return;
    setDownloading(true);
    setError("");
    try { await downloadAuditReport(audit); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Не удалось сформировать Excel-отчёт."); }
    finally { setDownloading(false); }
  };

  const reset = () => {
    setFile(null);
    setAudit(null);
    setStage("file");
    setProgress(emptyProgress);
    setError("");
    if (inputRef.current) inputRef.current.value = "";
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <div className="app-frame">
        <aside className="control-rail">
          <div className="identity">
            <span className="identity-mark" aria-hidden="true"><i /><i /><i /></span>
            <span className="identity-name">SL Call<br />Status Auditor</span>
          </div>
          <div className="rail-history">
            <JellyButton variant="rail" arrow onClick={() => setDrawerOpen(true)}>История загрузок</JellyButton>
          </div>
          <nav className="workflow" aria-label="Этапы работы">
            <WorkflowItem stage="file" current={stage} title="Исходный файл" detail="Ежедневная выгрузка" />
            <WorkflowItem stage="audit" current={stage} title="Проверка" detail={`Методология ${version}`} />
            <WorkflowItem stage="report" current={stage} title="Excel-отчёт" detail="Нарушения и контроль" />
          </nav>
        </aside>

        <main className="workbench">
          <header className="workbench-header">
            <div><h1>Ежедневный аудит</h1><p>Контроль соответствия статусов и комментариев менеджеров</p></div>
          </header>
          <section className="privacy-line" aria-label="Конфиденциальность">
            <span className="privacy-seal" aria-hidden="true"><i /></span>
            <div><strong>Закрытый контур обработки</strong><span>Данные остаются на компьютере и не отправляются в интернет</span></div>
          </section>

          <section className="operation" aria-labelledby="upload-title">
            <div className="operation-heading">
              <div><h2 id="upload-title">Поместите файл в рабочий контур</h2><p>Поддерживаются .xls, .xlsx и .xlsm. Анализируется первый лист книги.</p></div>
              <span className="operation-code">INPUT / XLS</span>
            </div>
            <div
              className={`dropzone ${dragging ? "is-dragging" : ""}`}
              role="button"
              tabIndex={0}
              onClick={() => inputRef.current?.click()}
              onKeyDown={(event) => (event.key === "Enter" || event.key === " ") && inputRef.current?.click()}
              onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(event) => { event.preventDefault(); setDragging(false); selectFile(event.dataTransfer.files[0]); }}
            >
              <input ref={inputRef} type="file" accept=".xls,.xlsx,.xlsm" hidden onChange={(event) => selectFile(event.target.files?.[0])} />
              <span className="drop-index" aria-hidden="true">FILE</span>
              <div className="drop-copy"><strong>Перетащите выгрузку в эту область</strong><span>или откройте файл с компьютера</span></div>
              <JellyButton type="button" onClick={(event) => { event.stopPropagation(); inputRef.current?.click(); }}>Открыть файл</JellyButton>
            </div>

            {file && (
              <div className="file-strip">
                <div className="file-strip-label"><span>Файл принят</span><strong>{file.name}</strong></div>
                <div className="file-strip-meta"><span>Параметры</span><strong>{extension} · {formatBytes(file.size)}</strong></div>
                <button className="text-button" onClick={() => inputRef.current?.click()}>Заменить файл</button>
              </div>
            )}
            {error && <div className="message error" role="alert"><strong>Не удалось выполнить операцию</strong><span>{error}</span></div>}

            <div className="command-line">
              <div className={`upload-toast ${toast ? "is-visible" : ""}`} role="status" aria-live="polite">
                <span className="toast-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M7 12.4 10.2 16 17.4 8.2" /></svg></span>
                <span>Отлично, ты на верном пути!</span>
              </div>
              <JellyButton variant="primary" arrow disabled={!file || busy} onClick={startAudit}>{busy ? "Проверяем файл" : "Запустить аудит"}</JellyButton>
            </div>
          </section>

          {busy && (
            <section className="process-board" aria-live="polite">
              <div className="process-copy"><div><h2>Проверяем события</h2><p>{progress.label}</p></div><strong>{Math.round(progress.percent)}%</strong></div>
              <div className="progress-track"><div style={{ transform: `scaleX(${progress.percent / 100})` }} /></div>
            </section>
          )}

          {audit && stage === "report" && (
            <section className="results-board" aria-labelledby="results-title">
              <header className="results-heading">
                <div><h2 id="results-title">Контрольный отчёт готов</h2><p>{audit.sourceName}</p></div>
                <JellyButton variant="primary" arrow disabled={downloading} onClick={download}>{downloading ? "Формируем отчёт" : "Скачать Excel-отчёт"}</JellyButton>
              </header>
              <div className="audit-facts">
                <span><b>{audit.metrics.total.toLocaleString("ru-RU")}</b> строк</span>
                <span><b>{formatDuration(audit.durationMs)}</b> обработка</span>
                <span><b>{audit.sheetName}</b> лист</span>
                <span><b>v{version}</b> правила</span>
              </div>
              <div className="metrics" aria-label="Результаты аудита">
                <article className="metric"><span>Все события</span><strong>{audit.metrics.total.toLocaleString("ru-RU")}</strong><small>строк обработано</small></article>
                <article className="metric"><span>Результативные</span><strong>{audit.metrics.checked.toLocaleString("ru-RU")}</strong><small>проверено системой</small></article>
                <article className="metric is-danger"><span>Несоответствия</span><strong>{audit.metrics.violations.toLocaleString("ru-RU")}</strong><small>явные нарушения</small></article>
                <article className="metric is-warning"><span>Ручной контроль *</span><strong>{audit.metrics.manual.toLocaleString("ru-RU")}</strong><small>не считается нарушением</small></article>
              </div>
              <div className="result-footnote"><strong>* Ручная проверка не является нарушением.</strong><span>Отчёт включает сводку, средний шаг звонков, отмеченные события, причины и правила.</span></div>
              <button className="text-button new-audit" onClick={reset}>Проверить следующий файл</button>
            </section>
          )}

          <footer className="workbench-footer"><span>Автономный аудит статусов</span><span>Методология v{version}</span></footer>
        </main>
      </div>
      <HistoryDrawer open={drawerOpen} history={history} onClose={() => setDrawerOpen(false)} />
    </>
  );
}
